import { Button, Form, Input, InputNumber, Select } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { FormInstance } from "antd/lib/form";
import { WebApiIntegrationsDtoExternalArticleDto } from "../../Repository/eventbookyapi";
import { TextService } from "../../Services/TextService";

const { Option } = Select;

interface SpirisInvoiceRow {
    articleNumber?: string;
    description?: string;
    amount?: number;
}

interface SpirisInvoiceRowsEditorProps {
    form: FormInstance;
    /** Form.List field name (default "rows"). */
    name?: string;
    articles: WebApiIntegrationsDtoExternalArticleDto[];
}

/**
 * Multi-line invoice-row editor for Spiris. Mirrors FortnoxInvoiceRowsEditor, minus the VAT
 * field - the Spiris invoice-draft row API has no per-row VAT, it's derived from the article.
 */
export const SpirisInvoiceRowsEditor = ({ form, name = "rows", articles }: SpirisInvoiceRowsEditorProps) => {
    const GetText = TextService.GetText;

    const onArticlePicked = (rowName: number, articleNumber: string | undefined) => {
        const article = articles.find(a => a.articleNumber === articleNumber);
        if (!article) return;
        const rows = form.getFieldValue(name) as SpirisInvoiceRow[];
        const next = [...rows];
        next[rowName] = {
            ...next[rowName],
            articleNumber,
            description: article.name ?? next[rowName]?.description,
            amount: article.salesPrice ?? next[rowName]?.amount,
        };
        form.setFieldValue(name, next);
    };

    return (
        <div style={{ maxWidth: 600, marginBottom: 12 }}>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>{GetText("SpirisInvoice_RowsHeading")}</div>
            <Form.List name={name}>
                {(fields, { add, remove }) => (
                    <>
                        {fields.map(({ key, name: rowName, ...rest }, idx) => (
                            <div key={key} style={{ border: "1px solid #e8e8e8", borderRadius: 6, marginBottom: 8, background: "#fafafa" }}>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 12px", borderBottom: "1px solid #e8e8e8" }}>
                                    <span style={{ fontWeight: 600, fontSize: 13 }}>{GetText("SpirisInvoice_RowLabel").replace("{0}", (idx + 1).toString())}</span>
                                    {fields.length > 1 && (
                                        <Button type="text" danger size="small" icon={<DeleteOutlined />} onClick={() => remove(rowName)}>
                                            {GetText("SpirisInvoice_RemoveRow")}
                                        </Button>
                                    )}
                                </div>
                                <div style={{ padding: "10px 12px" }}>
                                    {articles.length > 0 && (
                                        <Form.Item {...rest} name={[rowName, "articleNumber"]} label={GetText("SpirisInvoice_ArticleLabel")} style={{ marginBottom: 8 }}>
                                            <Select
                                                allowClear
                                                showSearch
                                                optionFilterProp="label"
                                                placeholder={GetText("SpirisInvoice_ArticlePlaceholder")}
                                                onChange={(v) => onArticlePicked(rowName, v)}
                                            >
                                                {articles.map(a => (
                                                    <Option key={a.articleNumber!} value={a.articleNumber!} label={`${a.articleNumber} ${a.name}`}>
                                                        {a.articleNumber} — {a.name}
                                                    </Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    )}
                                    <Form.Item {...rest} name={[rowName, "description"]} label={GetText("AddPayment_Description")} rules={[{ required: true, message: GetText("AddPaymentLink_DescriptionRequired") }]} style={{ marginBottom: 8 }}>
                                        <Input placeholder={"T.ex. 'Medlemsavgift 2026'"} />
                                    </Form.Item>
                                    <Form.Item {...rest} name={[rowName, "amount"]} label={GetText("SpirisInvoice_AmountLabel")} rules={[{ required: true, message: GetText("AddPaymentLink_AmountRequired") }]} style={{ marginBottom: 0 }}>
                                        <InputNumber min={0} max={1000000} style={{ width: 160 }} />
                                    </Form.Item>
                                </div>
                            </div>
                        ))}
                        <Button type="dashed" block icon={<PlusOutlined />} onClick={() => add({})}>
                            {GetText("SpirisInvoice_AddRow")}
                        </Button>
                    </>
                )}
            </Form.List>
        </div>
    );
};

/** Sum helper for the total line. rows is the Form.List value. */
export const sumSpirisRows = (rows: SpirisInvoiceRow[] | undefined) => {
    const list = Array.isArray(rows) ? rows : [];
    let total = 0;
    for (const r of list) {
        total += Number(r?.amount) || 0;
    }
    return total;
};
